/* eslint-disable @typescript-eslint/no-explicit-any */
import { Grid, Paper, Backdrop, SpeedDial, SpeedDialAction, SpeedDialIcon } from "@mui/material";
import { useEffect, useContext, useState, useCallback, Suspense, useRef } from "react";
import { Branding, EOBClaimLines, EOBHeader, EOBPrint, PatientDetails, ProviderDetails, SummaryDetails } from "../..";
import { AuthContext, ThemeContext } from "../../../../contexts";
import { useReactToPrint } from "react-to-print";
import { ClaimLines, EOBDetails } from "../../../../types/EOBDetails.types";
import { useQuery } from "../../../../hooks";
import { api } from "../../../../utils/api";
import { Helmet } from "react-helmet";

import DownloadIcon from "@mui/icons-material/Download";
import AttachEmailIcon from "@mui/icons-material/AttachEmail";

import "./EOB.css";
import { EOBActualProps, Summary } from "../../../../types/components/EOB.types";
import { useLocation } from "react-router-dom";
import { Member } from "../../../../types/employee.types";
import { trackPromise } from "react-promise-tracker";
import { GROUP_NUMBER } from "../../../../constants/groupNumber/groupNumber";

const EOB = (): JSX.Element => {
	const [openSpeedDial, setOpenSpeedDial] = useState<boolean>(false);
	const { theme } = useContext(ThemeContext);
	const location = useLocation();
	const componentRef = useRef(null);
	const handlePrint = useReactToPrint({
		content: () => componentRef.current
	});
	const speedDialActions = [
		{
			icon: <DownloadIcon />,
			name: "Download",
			callback: handlePrint
		}
	];
	const { member } = useContext(AuthContext);
	const [EOBDetails, setEOBDetails] = useState<EOBDetails>();
	const [summary, setSummary] = useState<Summary>();
	const query = useQuery();

	const getSummationByField = useCallback((claimLines: ClaimLines[], field: string) => {
		let summation = 0;
		claimLines.forEach((claimLine: any) => {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			summation = summation + claimLine[field as keyof ClaimLines]!;
		});
		return summation;
	}, []);

	const calculateSummaryDetails = useCallback(
		(claimLines: ClaimLines[]) => {
			const totalAmountCovered = getSummationByField(claimLines, "ADJCVRD");
			const paidByOtherInsuranceCo = getSummationByField(claimLines, "COBAMT");
			const totalPaidByPlan = getSummationByField(claimLines, "PLNRESP");
			const employeeResponsibility = getSummationByField(claimLines, "EERESP");
			const surcharge = getSummationByField(claimLines, "EERESP");
			const withhold = getSummationByField(claimLines, "EERESP");
			const copay = getSummationByField(claimLines, "EERESP");
			setSummary(
				Object.assign(
					{},
					{
						totalAmountCovered,
						paidByOtherInsuranceCo,
						totalPaidByPlan,
						employeeResponsibility,
						surcharge,
						withhold,
						copay
					}
				)
			);
		},
		[getSummationByField]
	);

	const getEOBDetails = useCallback(
		async (params): Promise<void> => {
			const _EOBDetails = await trackPromise(api.claim.getEOBDetails(params));
			setEOBDetails(Object.assign({}, _EOBDetails));
			calculateSummaryDetails(_EOBDetails.claimLines);
		},
		[calculateSummaryDetails]
	);

	useEffect(() => {
		console.log("member?.dependent_number", member?.dependent_number);
		if (member === null || member?.dependent_number === null) {
			return;
		}
		const pathname = location.pathname;
		let dependentNumber: number | undefined = undefined;
		if (pathname === "/dependent/claim/EOB") {
			if (query && query.get("dn") !== null && typeof query.get("dn") === "string") {
				dependentNumber = Number(atob(query.get("dn")));
			}
		} else {
			dependentNumber = member.dependent_number;
		}
		console.log("dependentNumber", dependentNumber);
		if (query && member && member?.dependent_number !== null && dependentNumber !== undefined) {
			const params = {
				claim_number: Number(query.get("number")),
				claim_pre: Number(query.get("pre")),
				claim_suffix: Number(query.get("suffix")),
				type: query.get("type") === "0" ? "CURRENT" : query.get("type") === "1" ? "HISTORY" : "HISTORY",
				dependent_number: dependentNumber
			};
			getEOBDetails(params);
		}
	}, [getEOBDetails, query, member, location.pathname]);

	const handleOpenSpeedDial = () => setOpenSpeedDial(true);

	const handleCloseSpeedDial = () => setOpenSpeedDial(false);
	return (
		<div className="EOB-root-container">
			{member.group_number === GROUP_NUMBER.Brookshire_Brothers ? (
				<Helmet>
					<title>Brookshire Brothers - EOB</title>
				</Helmet>
			) : member.group_number === GROUP_NUMBER.Intercity_Investment ? (
				<Helmet>
					<title>Intercity Investments - EOB</title>
				</Helmet>
			) : (
				<Helmet>
					<title>Nexcal Member Portal</title>
				</Helmet>
			)}
			<Backdrop open={openSpeedDial} style={{ zIndex: 1009 }} />
			<SpeedDial
				ariaLabel="SpeedDial tooltip example"
				className="speed-dial"
				icon={<SpeedDialIcon />}
				sx={{ position: "fixed", bottom: 16, right: 16 }}
				onClose={handleCloseSpeedDial}
				onOpen={handleOpenSpeedDial}
				open={openSpeedDial}
				FabProps={{
					style: {
						backgroundColor: theme?.primary_color
					}
				}}
			>
				{speedDialActions.map((action, index) => {
					const { name, icon, callback } = action;
					return (
						<SpeedDialAction key={index} icon={icon} tooltipTitle={name} tooltipOpen onClick={callback} />
					);
				})}
			</SpeedDial>
			{EOBDetails && summary ? (
				<>
					<EOBActual EOBDetails={EOBDetails} summary={summary} />
					<div style={{ display: "none" }}>
						<Suspense fallback={<div />}>
							<EOBPrint EOBDetails={EOBDetails} summary={summary} ref={componentRef} />
						</Suspense>
					</div>
				</>
			) : null}
		</div>
	);
};

const EOBActual = ({ EOBDetails, summary }: EOBActualProps): JSX.Element => {
	return (
		<div className="EOB">
			{EOBDetails ? (
				<div className="container" id="claim-pdf">
					<Paper variant="outlined" className="paper">
						<Suspense fallback={<div />}>
							<Branding administrator={EOBDetails.administered_by} />
						</Suspense>
						<Suspense fallback={<div />}>
							<EOBHeader />
						</Suspense>
						<Suspense fallback={<div />}>
							<PatientDetails
								claimant={EOBDetails.claimant}
								paidDate={EOBDetails.paid_date}
								checkNumber={EOBDetails.check_number}
							/>
						</Suspense>
						<Grid container spacing={7}>
							<Grid item xl={6} lg={6}>
								<Suspense fallback={<div />}>
									<ProviderDetails
										name={EOBDetails?.provider.name}
										full_provider_number={EOBDetails?.provider.full_provider_number}
										city={EOBDetails?.provider.city}
										state={EOBDetails?.provider.state}
										zipcode={EOBDetails?.provider.zipcode}
										address_line_1={EOBDetails.provider.address_line_1}
										PPO={EOBDetails.provider.PPO}
									/>
								</Suspense>
							</Grid>
							<Grid item xl={6} lg={6}>
								<Suspense fallback={<div />}>
									<SummaryDetails summary={summary} />
								</Suspense>
							</Grid>
						</Grid>
						<Suspense fallback={<div />}>
							<EOBClaimLines claimLines={EOBDetails.claimLines} />
						</Suspense>
					</Paper>
				</div>
			) : null}
		</div>
	);
};

export default EOB;
